import manifest from './portalTelemetryManifest.json';
import { appInsights } from './telemetryService';

export type PortalFeature = keyof typeof manifest.features;
export type PortalEvent = 'RouteViewed' | 'ScreenViewed' | 'StepViewed' | 'ActionSelected'
  | 'ApplicationSubmitted' | 'ValidationBlocked' | 'OperationStarted' | 'OperationCompleted' | 'PaymentReturn' | 'PaymentRedirect' | 'AccessBlocked';
export interface PortalContext { feature: PortalFeature; screen?: string }
interface Properties {
  screen?: string; step?: string; action?: string; operation?: string;
  outcome?: 'succeeded' | 'failed' | 'aborted';
  returnState?: 'success_screen' | 'cancelled' | 'unknown';
  mode?: 'new' | 'existing' | 'clone';
  direction?: 'next' | 'back' | 'dashboard';
}
const eventNames: PortalEvent[] = ['RouteViewed', 'ScreenViewed', 'StepViewed', 'ActionSelected',
  'ApplicationSubmitted', 'ValidationBlocked', 'OperationStarted', 'OperationCompleted', 'PaymentReturn', 'PaymentRedirect', 'AccessBlocked'];

export function portalTelemetryEnabled(feature: PortalFeature) {
  const config = manifest.features[feature];
  return Boolean(config && process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING)
    && process.env.REACT_APP_PORTAL_TELEMETRY_ENABLED !== 'false'
    && process.env[config.toggle] !== 'false';
}

// Match known first path segments; never emit query strings or record IDs.
export function portalRouteContext(pathname: string): PortalContext | undefined {
  const segment = '/' + (pathname.split('/')[1] || '').toLowerCase();
  for (const feature of Object.keys(manifest.features) as PortalFeature[]) {
    const route = manifest.features[feature].routes.find(value => value.toLowerCase() === segment);
    if (route) return { feature, screen: route.toLowerCase() };
  }
}

export function currentPortalContext(): PortalContext | undefined {
  if (typeof window === 'undefined') return undefined;
  const direct = portalRouteContext(window.location.pathname);
  if (direct) return direct;
  if (!/^\/(payment-success|membership-direct-debit)(\/|$)/i.test(window.location.pathname)) return undefined;
  const query = new URLSearchParams(window.location.search);
  const target = query.get('retry-url') || query.get('cancelUrl');
  if (!target) return undefined;
  try {
    const url = new URL(target, window.location.origin);
    if (url.origin !== window.location.origin) return undefined;
    const context = portalRouteContext(url.pathname);
    if (context && ['affiliateAssociate', 'membershipOnboarding', 'membershipRenewal', 'membershipTransfer'].includes(context.feature)) {
      return { feature: context.feature, screen: window.location.pathname.split('/').slice(0, 2).join('/').toLowerCase() };
    }
  } catch { /* Malformed query data cannot affect the application. */ }
  return undefined;
}

/** Static metadata only: no values, URLs, filenames, IDs, errors, arguments or payloads. */
export function trackPortalEvent(context: PortalContext | undefined, name: PortalEvent,
  properties: Properties = {}, durationMs?: number) {
  if (!context || !portalTelemetryEnabled(context.feature) || !eventNames.includes(name)) return;
  try {
    const config = manifest.features[context.feature];
    const safe: Record<string, string> = { feature: context.feature, schemaVersion: '1' };
    const screens: string[] = [...config.screens, ...config.routes.map(route => route.toLowerCase()),
      '/payment-success', '/membership-direct-debit', 'Unauthorized'];
    const screen = properties.screen || context.screen;
    if (screen && screens.includes(screen)) safe.screen = screen;
    if (properties.step && (config.steps as string[]).includes(properties.step)) safe.step = properties.step;
    if (properties.action && (config.actions as string[]).includes(properties.action)) safe.action = properties.action;
    if (properties.operation && config.operations.some(prefix =>
      manifest.operations[prefix as keyof typeof manifest.operations] === properties.operation)) safe.operation = properties.operation;
    if (properties.outcome && ['succeeded', 'failed', 'aborted'].includes(properties.outcome)) safe.outcome = properties.outcome;
    if (properties.returnState && ['success_screen', 'cancelled', 'unknown'].includes(properties.returnState)) safe.returnState = properties.returnState;
    if (properties.mode && ['new', 'existing', 'clone'].includes(properties.mode)) safe.mode = properties.mode;
    if (properties.direction && ['next', 'back', 'dashboard'].includes(properties.direction)) safe.direction = properties.direction;
    appInsights.trackEvent({ name: `Portal.SPA.${context.feature}.${name}`, properties: safe,
      measurements: typeof durationMs === 'number' && Number.isFinite(durationMs)
        ? { durationMs: Math.max(0, durationMs) } : {} });
  } catch { /* Telemetry must never interrupt the SPA. */ }
}

// Preserve callback arguments, return values and undefined handlers.
export function portalAction<T extends (...args: any[]) => any>(context: PortalContext | undefined,
  action: string, handler: T): T;
export function portalAction<T extends (...args: any[]) => any>(context: PortalContext | undefined,
  action: string, handler: T | undefined): T | undefined;
export function portalAction(context: PortalContext | undefined, action: string,
  handler: ((...args: any[]) => any) | undefined) {
  if (!handler) return handler;
  return (...args: any[]) => {
    trackPortalEvent(context, 'ActionSelected', { action });
    return handler(...args);
  };
}

export function portalScreenContext(screen: string): PortalContext | undefined {
  const context = currentPortalContext();
  return context ? { ...context, screen } : undefined;
}

// PCS enrolment and reenrolment journey helpers share the portal App Insights client.
export type JourneyMode = 'enrolment' | 'reenrolment';
export type PaymentOption = 'pcs_only' | 'combined' | 'prepaid';
export const pcsSteps = ['registration', 'personal', 'education', 'work', 'payment'] as const;
type PcsEventName = 'JourneyStarted' | 'JourneyResumed' | 'StepViewed' | 'Navigation'
  | 'ValidationBlocked' | 'EligibilityBlocked' | 'ApplicationPending'
  | 'WorkDetailsConfirmed' | 'PaymentOptionSelected' | 'PaymentAttempt'
  | 'PaymentRedirect' | 'PaymentReturn' | 'PaymentRetry' | 'PrepaidDisplayed'
  | 'ApplicationSubmitted' | 'OperationCompleted';
export interface PcsProperties {
  step?: typeof pcsSteps[number];
  direction?: 'next' | 'back' | 'payment_options';
  paymentOption?: PaymentOption;
  outcome?: 'succeeded' | 'failed' | 'aborted' | 'success_screen' | 'cancelled'
    | 'unknown' | 'returned_to_payment' | 'invalid_redirect';
  operation?: string;
  correlation?: 'matched' | 'unmatched';
}
export interface JourneyContext {
  journeyId: string;
  mode: JourneyMode;
  attemptId?: string;
}
interface PcsJourney extends JourneyContext {
  expiresAt: number;
  seen: string[];
}
const pcsStorageKey = 'pcs.telemetry.v1';
const pcsLifetimeMs = 2 * 60 * 60 * 1000;
let pcsJourneyMemory: PcsJourney | undefined;
let pcsResumedId: string | undefined;
const pcsIdPattern = /^[a-f0-9-]{36}$/i;

export function pcsTelemetryEnabled() {
  return Boolean(process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING)
    && process.env.REACT_APP_PCS_TELEMETRY_ENABLED !== 'false';
}

export function pcsRouteMode(path = window.location.pathname): JourneyMode | undefined {
  if (/^\/pcsenrolment\/?$/i.test(path)) return 'enrolment';
  if (/^\/pcsreenrolment\/?$/i.test(path)) return 'reenrolment';
  return undefined;
}

function randomPcsJourneyId() {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  const hex = Array.from(bytes, n => n.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function readPcsJourney(): PcsJourney | undefined {
  if (!pcsTelemetryEnabled()) return undefined;
  try {
    const value = JSON.parse(sessionStorage.getItem(pcsStorageKey) || 'null');
    if (value && pcsIdPattern.test(value.journeyId)
      && ['enrolment', 'reenrolment'].includes(value.mode)
      && value.expiresAt > Date.now() && value.expiresAt <= Date.now() + pcsLifetimeMs
      && Array.isArray(value.seen) && value.seen.every((key: unknown) => typeof key === 'string')
      && (!value.attemptId || pcsIdPattern.test(value.attemptId))) pcsJourneyMemory = value;
  } catch { /* Storage may be unavailable; retain the in-pcsJourneyMemory journey. */ }
  if (pcsJourneyMemory && pcsJourneyMemory.expiresAt <= Date.now()) pcsJourneyMemory = undefined;
  return pcsJourneyMemory;
}

function savePcsJourney(journey: PcsJourney) {
  pcsJourneyMemory = journey;
  try { sessionStorage.setItem(pcsStorageKey, JSON.stringify(journey)); } catch { /* Best effort. */ }
}

export function currentPcsJourney(): JourneyContext | undefined {
  const journey = readPcsJourney();
  return journey && { journeyId: journey.journeyId, mode: journey.mode, attemptId: journey.attemptId };
}

/** Only explicit, bounded properties are emitted. Never pass forms, actions, errors or URLs. */
export function trackPcsEvent(name: PcsEventName, properties: PcsProperties = {},
  measurements: { durationMs?: number } = {}, context: JourneyContext | null | undefined = currentPcsJourney()) {
  if (!pcsTelemetryEnabled()) return;
  try {
    const safe: Record<string, string> = {
      feature: 'pcsEnrolment', schemaVersion: '1',
      environment: process.env.REACT_APP_ENVIRONMENT || process.env.REACT_APP_ENV || 'unspecified',
      mode: context?.mode || 'unknown'
    };
    if (context) safe.journeyId = context.journeyId;
    if (context?.attemptId) safe.attemptId = context.attemptId;
    const keys: (keyof PcsProperties)[] = ['step', 'direction', 'paymentOption', 'outcome', 'operation', 'correlation'];
    keys.forEach(key => { if (properties[key] !== undefined) safe[key] = properties[key]!; });
    const durationMs = measurements.durationMs;
    appInsights.trackEvent({ name: `PCS.Enrolment.${name}`, properties: safe,
      measurements: typeof durationMs === 'number' && Number.isFinite(durationMs)
        ? { durationMs: Math.max(0, durationMs) } : {} });
  } catch { /* Observability must never interrupt enrolment. */ }
}

export function startPcsJourney(mode: JourneyMode): JourneyContext | undefined {
  if (!pcsTelemetryEnabled()) return undefined;
  try {
    let journey = readPcsJourney();
    if (!journey || journey.mode !== mode) {
      journey = { journeyId: randomPcsJourneyId(), mode, expiresAt: Date.now() + pcsLifetimeMs, seen: [] };
      savePcsJourney(journey);
      pcsResumedId = journey.journeyId;
      trackPcsEvent('JourneyStarted', {}, {}, journey);
    } else if (pcsResumedId !== journey.journeyId) {
      pcsResumedId = journey.journeyId;
      trackPcsEvent('JourneyResumed', {}, {}, journey);
    }
    return currentPcsJourney();
  } catch { return undefined; }
}

/** Milestones count once per journey, including reloads and Strict Mode remounts. */
export function trackPcsOnce(name: PcsEventName, key: string, properties: PcsProperties = {},
  measurements: { durationMs?: number } = {}) {
  const journey = readPcsJourney();
  if (!journey || journey.seen.includes(key)) return;
  journey.seen.push(key);
  savePcsJourney(journey);
  trackPcsEvent(name, properties, measurements, journey);
}

export function beginPcsPayment(paymentOption: PaymentOption) {
  const journey = readPcsJourney();
  if (!journey) return;
  try {
    journey.attemptId = randomPcsJourneyId();
    savePcsJourney(journey);
    trackPcsEvent('PaymentAttempt', { paymentOption });
  } catch { /* Best effort. */ }
}

export function trackPcsReturn(outcome: PcsProperties['outcome']) {
  const journey = currentPcsJourney();
  if (journey?.attemptId) {
    trackPcsOnce('PaymentReturn', `return:${journey.attemptId}`,
      { outcome, correlation: 'matched' });
  } else {
    // No guessed association for direct links, expired storage or another tab.
    trackPcsEvent('PaymentReturn', { outcome, correlation: 'unmatched' }, {}, null);
  }
}

export function flushPcsTelemetry() {
  if (!pcsTelemetryEnabled()) return;
  try {
    Promise.resolve(appInsights.flush(true)).catch(() => { /* Best effort. */ });
  } catch { /* Do not delay payment navigation. */ }
}

/** Track a form save through the existing App Insights client, without Redux middleware. */
export async function trackPcsSaveAsync<T extends { meta: { requestStatus: string; aborted?: boolean }; payload?: unknown }>(
  operation: 'registration_save' | 'education_save' | 'personal_details_save' | 'personal_details_verify' | 'work_details_save',
  save: () => Promise<T>, step?: PcsProperties['step']
): Promise<T> {
  const context = currentPcsJourney();
  const started = performance.now();
  try {
    const response = await save();
    const outcome = response.meta.requestStatus === 'fulfilled' && response.payload !== false ? 'succeeded'
      : response.meta.aborted ? 'aborted' : 'failed';
    const properties = { operation, step, outcome } as const;
    const measurements = { durationMs: performance.now() - started };
    trackPcsEvent('OperationCompleted', properties, measurements, context);
    if (outcome === 'succeeded') trackPcsEvent('ApplicationSubmitted', properties, measurements, context);
    return response;
  } catch (error) {
    trackPcsEvent('OperationCompleted', { operation, step, outcome: 'failed' },
      { durationMs: performance.now() - started }, context);
    throw error;
  }
}
